import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAgentResponse } from '../../../helper/agentHelper';
// import { changeLoginModalVisibility } from '../../../redux/auth/authAction';
import { submitContactForm } from '../../../redux/contactUs/contactUsActions';
import { fetchUserData, clearUserData } from '../../../redux/agent/agentActions';
import decode from 'jwt-decode';
import { IMAGES_BASE_URL } from '../../../settings';
import {
  CHATBOT_CONFIG,
  CHAT_STEPS,
  MESSAGE_TYPES,
  CSS_CLASSES,
  WELCOME_MESSAGES,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  INFO_MESSAGES,
  BUTTON_ACTIONS,
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  FORM_BUTTONS,
  LOADING_MESSAGES,
  VALIDATION_RULES,
  UI_CONSTANTS,
  DISCLAIMER_MESSAGES,
  INPUT_TYPES,
  FORM_FIELD_NAMES,
  CONTACT_FORM_MESSAGES,
  CHAT_COMMANDS,
  INFO_PANEL_MESSAGES,
  HELP_INFORMATION,
  WORKSPACE_MESSAGES,
  LOCATION_MESSAGES,
  PRICE_RANGE_MESSAGES,
  SPACE_SELECTION_MESSAGES,
  FOLLOW_UP_MESSAGES,
} from '../constants';
import { fetchUserDataAndBookings, fetchWorkspaceOptions, searchAvailableSpaces } from '../utils/chatbotApiUtils';
import { getCountryFromUrl, getCountryNameFromCode, constructBookingUrl, shouldHideChatbot } from '../utils/urlUtils';
import {
  generatePriceRanges,
  filterSpacesByLocation,
  filterSpacesByPriceRange,
  buildGeminiContext,
  validateUserInput,
  formatTimestamp,
  getCategoryFromEventType,
} from '../utils/dataProcessingUtils';
import {
  getInitialContactFormState,
  validateContactForm,
  sanitizeFormData,
  handleFormFieldChange,
  resetForm,
  formatFormDataForSubmission,
  isFormReadyToSubmit,
} from '../utils/formUtils';
import {
  createMessage,
  createLoadingMessage,
  createPersonalizedWelcomeMessage,
  createDefaultWelcomeMessage,
  createFallbackWelcomeMessage,
  createSecondaryWelcomeMessage,
  isBookingProcessQuestion,
  isHowToBookQuestion,
  isBookingRequest,
  isWorkspaceOptionsQuestion,
  generateWorkspaceOptionsText,
  generateBookingProcessText,
  generateBookingStepsText,
  generateBookingRestrictionMessage,
  generateWorkspaceTypeRestrictionMessage,
  generateContactAgentMessage,
  generateHelpMessage,
  generateErrorMessage,
  generateContactFormSuccessMessage,
  generateContactFormErrorMessage,
  generateFormValidationErrorMessage,
} from '../utils/chatbotUtils';
import useSpaceyIcon from '../../../hooks/useSpaceyIcon';
import '../../../assets/scss/components/Agent/ChatbotPopup.scss';

const ChatbotPopup = React.memo(({ isOpen, onClose }) => {
  console.log('ChatbotPopup: isOpen', isOpen);
  const spaceyIconSrc = useSpaceyIcon();

  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(CHAT_STEPS.INITIAL);
  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [workspaceOptionsLoading, setWorkspaceOptionsLoading] = useState(false);
  const [workspaceOptionsLoaded, setWorkspaceOptionsLoaded] = useState(false);
  const [workspaceOptionsSource, setWorkspaceOptionsSource] = useState('none'); // 'redux', 'api', or 'none'
  const [showContactForm, setShowContactForm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [contactForm, setContactForm] = useState(getInitialContactFormState());
  const [contactFormErrors, setContactFormErrors] = useState({});
  const [previousBookingsCount, setPreviousBookingsCount] = useState(0);
  const [initialMessageSet, setInitialMessageSet] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [selectedWorkspaceType, setSelectedWorkspaceType] = useState(null);
  const [availableSpaces, setAvailableSpaces] = useState([]);
  const [userName, setUserName] = useState('');
  const [hasDeclinedPreviousBooking, setHasDeclinedPreviousBooking] = useState(false);
  const messagesEndRef = useRef(null);

  // Load declined state from localStorage on component mount
  useEffect(() => {
    const declinedState = localStorage.getItem('hasDeclinedPreviousBooking');
    if (declinedState === 'true') {
      setHasDeclinedPreviousBooking(true);
    }
  }, []);

  // Function to set declined state and persist to localStorage
  const setDeclinedAndPersist = (value) => {
    setHasDeclinedPreviousBooking(value);
    localStorage.setItem('hasDeclinedPreviousBooking', value.toString());
  };

  // Function to clear declined state from localStorage
  const clearDeclinedState = () => {
    setHasDeclinedPreviousBooking(false);
    localStorage.removeItem('hasDeclinedPreviousBooking');
  };

  const dispatch = useDispatch();

  // Authentication state
  const authDetails = useSelector((state) => state?.auth?.loginDetails?.data?.token || null);
  const isLoggedIn = !!authDetails;

  // Debug authentication state
  useEffect(() => {
    console.log('Chatbot: Authentication state changed:', {
      authDetails: authDetails ? 'available' : 'null',
      isLoggedIn,
      token: authDetails,
    });
  }, [authDetails, isLoggedIn]);

  // Redux state for workspace types - multiple sources to check
  const spaceListingTypes = useSelector((state) => state?.spaceListing?.spaceTypes || []);
  const spaceActivities = useSelector((state) => state?.activity?.data || []);
  const searchResultTypes = useSelector((state) => state?.searchResult?.spaceActivities?.data || []);
  const homePageTypes = useSelector((state) => state?.home?.homepage?.data?.spaceActivities || []);

  // Redux state for user data (now part of agent state)
  const userData = useSelector((state) => state?.agent?.userData || null);
  const userDataLoading = useSelector((state) => state?.agent?.userDataLoading || false);
  const userDataError = useSelector((state) => state?.agent?.userDataError || null);

  // Function to get workspace options from Redux state
  const getWorkspaceOptionsFromRedux = () => {
    // Priority order: spaceListing > spaceActivities > searchResult > homePage
    let workspaceData = null;

    if (spaceListingTypes && spaceListingTypes.length > 0) {
      workspaceData = spaceListingTypes;
      console.log('Chatbot: Using workspace types from spaceListing Redux state');
    } else if (spaceActivities && spaceActivities.length > 0) {
      workspaceData = spaceActivities;
      console.log('Chatbot: Using workspace types from spaceActivities Redux state');
    } else if (searchResultTypes && searchResultTypes.length > 0) {
      workspaceData = searchResultTypes;
      console.log('Chatbot: Using workspace types from searchResult Redux state');
    } else if (homePageTypes && homePageTypes.length > 0) {
      workspaceData = homePageTypes;
      console.log('Chatbot: Using workspace types from homePage Redux state');
    }

    if (!workspaceData) {
      console.log('Chatbot: No workspace types found in Redux state');
      return null;
    }

    // Process the data to match the expected format
    const options = [];
    workspaceData.forEach((category) => {
      if (category.venueType && Array.isArray(category.venueType)) {
        category.venueType.forEach((venue) => {
          options.push({
            label: venue.name,
            value: venue.name,
            code: venue.code,
            eventTypeId: venue.id,
            description: venue.description,
            category: category.name,
            rateUnit: venue.rateUnit || 'hour',
            rate: venue.rate || 0,
          });
        });
      }
    });

    console.log(`Chatbot: Processed ${options.length} workspace options from Redux`);
    return options.length > 0 ? options : null;
  };

  // Function to refresh workspace options from Redux
  const refreshWorkspaceOptionsFromRedux = () => {
    const reduxOptions = getWorkspaceOptionsFromRedux();
    if (reduxOptions && reduxOptions.length > 0) {
      setWorkspaceOptions(reduxOptions);
      setWorkspaceOptionsLoaded(true);
      setWorkspaceOptionsSource('redux');
      return true;
    }
    return false;
  };

  // Function to manually refresh workspace options (useful for debugging)
  const manualRefreshWorkspaceOptions = () => {
    console.log('Chatbot: Manually refreshing workspace options from Redux...');
    if (refreshWorkspaceOptionsFromRedux()) {
      console.log('Chatbot: Successfully refreshed from Redux');
    } else {
      console.log('Chatbot: No data in Redux, falling back to API call');
      setWorkspaceOptionsSource('api');
      fetchWorkspaceOptionsLocal();
    }
  };

  // Helper function to determine if price ranges should be skipped
  const shouldSkipPriceRanges = (spaces) => {
    if (!spaces || spaces.length === 0) return false;
    if (spaces.length === 1) return true;

    // Check if all spaces have the same price
    const uniquePrices = [...new Set(spaces.map((space) => space.rate))];
    return uniquePrices.length === 1;
  };

  // Helper function to generate message when price ranges are skipped
  const generateSkipPriceRangeMessage = (spaces) => {
    if (spaces.length === 1) {
      return `Here's the space that matches your criteria:`;
    } else {
      return `Here are the spaces that match your criteria:`;
    }
  };

  // Helper function to validate and clean space data
  const validateAndCleanSpaces = (spaces) => {
    if (!spaces || !Array.isArray(spaces)) return [];

    return spaces
      .filter((space) => {
        // Ensure space has required properties
        return space && space.name && space.rate !== undefined && space.rate !== null && !isNaN(space.rate);
      })
      .map((space) => ({
        ...space,
        rate: Number(space.rate) || 0, // Ensure rate is a number
        name: space.name.trim(), // Clean name
      }));
  };

  // Function to fetch user data and previous bookings count
  const fetchUserDataAndBookingsLocal = async () => {
    try {
      // Check if user data already exists in Redux store
      if (userData && userData.userName && userData.bookingCount !== undefined) {
        // Use existing Redux data
        setPreviousBookingsCount(userData.bookingCount || 0);
        setUserName(userData.userName || '');
        return userData;
      }

      // If no Redux data, fetch from API and store in Redux
      const result = await dispatch(fetchUserData());

      // Set local state from Redux result
      setPreviousBookingsCount(result.bookingCount || 0);
      setUserName(result.userName || '');

      return result;
    } catch (error) {
      return { bookingCount: 0, userName: '' };
    }
  };

  // Function to fetch workspace options from API
  const fetchWorkspaceOptionsLocal = async () => {
    setWorkspaceOptionsLoading(true);
    try {
      // First, try to get workspace options from Redux state
      const reduxOptions = getWorkspaceOptionsFromRedux();

      if (reduxOptions && reduxOptions.length > 0) {
        // Use data from Redux - no API call needed
        setWorkspaceOptions(reduxOptions);
        setWorkspaceOptionsLoaded(true);
        setWorkspaceOptionsSource('redux');
        setWorkspaceOptionsLoading(false);
        return;
      }

      // If Redux doesn't have data, fall back to API call
      const country = getCountryFromUrl();
      const result = await fetchWorkspaceOptions(country);

      if (result.success && result.hasOptions) {
        setWorkspaceOptions(result.options);
        setWorkspaceOptionsLoaded(true);
        setWorkspaceOptionsSource('api');
      } else {
        setWorkspaceOptionsLoaded(true);
        setWorkspaceOptionsSource('api');
        setMessages((prev) => [
          ...prev,
          {
            id: prev.length + 1,
            text: 'Sorry, no workspace options are currently available. Please try again later.',
            sender: 'bot',
            timestamp: new Date(),
          },
        ]);
      }
    } catch (error) {
      setWorkspaceOptionsLoaded(true);
      setWorkspaceOptionsSource('api');
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          text: "Sorry, I'm having trouble loading workspace options. Please try again later.",
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setWorkspaceOptionsLoading(false);
    }
  };

  // Handle Yes option for previous booking
  const handlePreviousBookingYes = async () => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.previousEventType) {
      // Add user's "Yes" message
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          text: 'Yes',
          sender: 'user',
          timestamp: new Date(),
        },
      ]);

      // Show loading message
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          text: 'Searching for spaces that match your criteria...',
          sender: 'bot',
          timestamp: new Date(),
          isLoading: true,
        },
      ]);

      try {
        // Get workspace options to find the matching event type
        let currentWorkspaceOptions = workspaceOptions;
        if (!workspaceOptionsLoaded || workspaceOptions.length === 0) {
          const reduxOptions = getWorkspaceOptionsFromRedux();
          if (reduxOptions && reduxOptions.length > 0) {
            currentWorkspaceOptions = reduxOptions;
          } else {
            const country = getCountryFromUrl();
            const result = await fetchWorkspaceOptions(country);
            if (result.success && result.hasOptions) {
              currentWorkspaceOptions = result.options;
            }
          }
        }

        // Find the workspace option that matches the previous eventType
        const matchingOption = currentWorkspaceOptions.find((option) => {
          const optionLabel = option.label.toLowerCase();
          const previousType = lastMessage.previousEventType.toLowerCase();
          return (
            optionLabel.includes(previousType) ||
            previousType.includes(optionLabel) ||
            optionLabel.replace(/\s+/g, '').includes(previousType.replace(/\s+/g, '')) ||
            previousType.replace(/\s+/g, '').includes(optionLabel.replace(/\s+/g, ''))
          );
        });

        if (matchingOption) {
          // Set the selected workspace type
          setSelectedWorkspaceType(matchingOption);

          // Get country from URL or default to LK
          const country = getCountryFromUrl();

          // Call the search/map API to get actual spaces
          const searchParams = {
            category: getCategoryFromEventType(matchingOption.label),
            country: country,
            eventType: matchingOption.code,
            x: 80.0087746,
            y: 6.901608599999999,
          };

          const searchResult = await searchAvailableSpaces(searchParams);

          // Remove loading message
          setMessages((prev) => prev.filter((msg) => !msg.isLoading));

          if (searchResult.success && searchResult.spaces && searchResult.spaces.length > 0) {
            // Get the specific space ID from userData
            const previousSpaceId = userData.lastBookedSpace?.space;

            // Filter to find the exact space the user previously booked
            const previousSpace = searchResult.spaces.find((space) => space.id === previousSpaceId);

            if (previousSpace) {
              // Set available spaces (just the previous space)
              setAvailableSpaces([previousSpace]);

              // Update step to show recommended spaces
              setStep(4);

              // Add bot message with the specific previous space
              setMessages((prev) => [
                ...prev,
                {
                  id: prev.length + 1,
                  text: "Here's the space that matches your criteria:",
                  sender: 'bot',
                  timestamp: new Date(),
                  recommendedSpaces: [previousSpace],
                  selectedWorkspace: matchingOption, // Store the workspace type for booking URL construction
                },
              ]);
            } else {
              // Previous space not found in current results, show top 5 spaces
              setAvailableSpaces(searchResult.spaces);
              const recommendedSpaces = searchResult.spaces.slice(0, 5);

              // Update step to show recommended spaces
              setStep(4);

              // Add bot message with recommended spaces
              setMessages((prev) => [
                ...prev,
                {
                  id: prev.length + 1,
                  text: "Here's the space that matches your criteria:",
                  sender: 'bot',
                  timestamp: new Date(),
                  recommendedSpaces: recommendedSpaces,
                  selectedWorkspace: matchingOption, // Store the workspace type for booking URL construction
                },
              ]);
            }
          } else {
            // No spaces found
            setMessages((prev) => [
              ...prev,
              {
                id: prev.length + 1,
                text: 'Sorry, no spaces found for your previous booking type. Let me help you find something else.',
                sender: 'bot',
                timestamp: new Date(),
              },
            ]);
            setStep(1);
          }
        } else {
          // No matching workspace type found
          setMessages((prev) => [
            ...prev,
            {
              id: prev.length + 1,
              text: "Sorry, I couldn't find the workspace type from your previous booking. Let me help you find something else.",
              sender: 'bot',
              timestamp: new Date(),
            },
          ]);
          setStep(1);
        }
      } catch (error) {
        console.error('Error in previous booking yes:', error);

        // Remove loading message
        setMessages((prev) => prev.filter((msg) => !msg.isLoading));

        setMessages((prev) => [
          ...prev,
          {
            id: prev.length + 1,
            text: 'Sorry, I encountered an error. Let me help you find something else.',
            sender: 'bot',
            timestamp: new Date(),
          },
        ]);
        setStep(1);
      }
    }
  };

  // Handle No option for previous booking
  const handlePreviousBookingNo = () => {
    // Set flag to prevent showing previous booking question again
    setDeclinedAndPersist(true);

    // Reset chat and show personalized message again
    setMessages([]);
    setStep(1);
    setInitialMessageSet(false);

    // Show personalized message immediately using Redux data
    if (isLoggedIn && userData) {
      // Use Redux user data instead of making API call
      const displayName = userData.userName || 'there';
      const bookingCount = userData.bookingCount || 0;

      // Show the personalized welcome message again (without previous booking question)
      setMessages([createPersonalizedWelcomeMessage(displayName, bookingCount)]);
      setStep(1); // This will show workspace options directly
      setInitialMessageSet(true);
    } else {
      // For non-logged in users, show the default message
      setMessages([createDefaultWelcomeMessage()]);
      setInitialMessageSet(true);
    }
  };

  // Handle popup close
  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleBackdropClick = () => {
    handleClose();
  };

  const stopPopupClickPropagation = (event) => {
    event.stopPropagation();
  };

  // Handle login
  const handleLogin = () => {
    handleClose();
    try {
      // Open the main auth flow explicitly in login mode
      const evt = new CustomEvent('ms-open-auth', { detail: { mode: 'login' } });
      window.dispatchEvent(evt);
    } catch (e) {
      // no-op: fallback could be added if needed
    }
  };

  // Handle reload chat
  const handleReloadChat = () => {
    setMessages([]);
    setStep(1);
    setInitialMessageSet(false);

    clearDeclinedState(); // Clear declined state on reload
    setSelectedWorkspaceType(null);
    setAvailableSpaces([]);
    setSelectedSpace(null);
    setShowContactForm(false);
    setShowInfo(false);
    setInputMessage('');
    resetForm(setContactForm);
    setContactFormErrors({});
  };

  // Handle workspace selection
  const handleWorkspaceSelect = async (option) => {
    const userMsg = createMessage(option, 'user');

    // Add user message immediately
    setMessages((prev) => [...prev, userMsg]);

    // Update step immediately to prevent flickering
    setStep(2);

    // Show loading spinner
    setLoading(true);
    setMessages((prev) => [...prev, createLoadingMessage('Searching for available spaces...')]);

    try {
      // Find the selected workspace option to get code and category
      const selectedWorkspace = workspaceOptions.find((ws) => ws.value === option);

      if (!selectedWorkspace) {
        throw new Error('Workspace option not found');
      }

      // Store the selected workspace type for context
      setSelectedWorkspaceType(selectedWorkspace);

      // Get country from URL or default to LK
      const country = getCountryFromUrl();

      // Call the search/map API to get actual spaces
      const searchParams = {
        category: getCategoryFromEventType(selectedWorkspace.label),
        country: country,
        eventType: selectedWorkspace.code,
        x: 80.0087746,
        y: 6.901608599999999,
      };

      const searchResult = await searchAvailableSpaces(searchParams);

      if (!searchResult.success || !searchResult.hasLocations) {
        // Replace loading message with no results message and find something else option
        setMessages((prev) => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            id: newMessages[newMessages.length - 1].id,
            text: `No locations found for the selected workspace type: ${option}`,
            sender: 'bot',
            timestamp: new Date(),
            showFindSomethingElse: true,
          };
          return newMessages;
        });
        // Reset step to 1 if no locations found
        setStep(1);
      } else {
        // Replace loading message with location selection question
        setMessages((prev) => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            id: newMessages[newMessages.length - 1].id,
            text: 'Great choice! Where would you like to find this workspace?',
            sender: 'bot',
            timestamp: new Date(),
            dynamicRateOptions: searchResult.rateOptions,
            dynamicLocationOptions: searchResult.locationOptions,
            availableSpaces: searchResult.spaces,
            selectedWorkspace: selectedWorkspace, // Store the selected workspace for later use
          };
          return newMessages;
        });

        // Step is already set to 2, no need to set it again
      }
    } catch (error) {
      // Replace loading message with specific error message
      setMessages((prev) => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          id: newMessages[newMessages.length - 1].id,
          text: error.message || "I'm having trouble searching for spaces right now. Please try again later.",
          sender: 'bot',
          timestamp: new Date(),
        };
        return newMessages;
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle location selection
  const handleLocationSelect = async (location) => {
    // Capitalize first letter for display purposes
    const displayLocation = location.charAt(0).toUpperCase() + location.slice(1).toLowerCase();

    const userMsg = {
      id: messages.length + 1,
      text: displayLocation,
      sender: 'user',
      timestamp: new Date(),
    };

    // Add user message immediately
    setMessages((prev) => [...prev, userMsg]);

    // Update step immediately to prevent flickering
    setStep(3);

    // Show loading spinner
    setLoading(true);
    setMessages((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        text: 'Finding spaces for your selection...',
        sender: 'bot',
        timestamp: new Date(),
        isLoading: true,
      },
    ]);

    try {
      // Get the available spaces from the previous message
      const previousMessage = messages.find((msg) => msg.availableSpaces);
      const availableSpaces = previousMessage?.availableSpaces || [];

      // Store available spaces for context
      setAvailableSpaces(availableSpaces);

      // Only proceed if we have actual spaces data
      if (availableSpaces.length === 0) {
        throw new Error('No available spaces data found');
      }

      // Filter spaces based on selected location
      const filteredSpaces = filterSpacesByLocation(availableSpaces, location);

      // Only proceed if we have filtered spaces
      if (filteredSpaces.length === 0) {
        // Replace loading message with no results message and find something else option
        setMessages((prev) => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            id: newMessages[newMessages.length - 1].id,
            text: `No spaces found for the selected location: ${location}`,
            sender: 'bot',
            timestamp: new Date(),
            showFindSomethingElse: true,
          };
          return newMessages;
        });
      } else {
        // Validate and clean the filtered spaces
        const validSpaces = validateAndCleanSpaces(filteredSpaces);

        if (validSpaces.length === 0) {
          // No valid spaces after validation
          setMessages((prev) => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = {
              id: newMessages[newMessages.length - 1].id,
              text: `Found spaces but they don't have valid pricing information. Please try a different location or contact our support team.`,
              sender: 'bot',
              timestamp: new Date(),
              showFindSomethingElse: true,
            };
            return newMessages;
          });
        } else {
          // Smart price range logic
          if (shouldSkipPriceRanges(validSpaces)) {
            // Skip price ranges - show spaces directly
            const messageText = generateSkipPriceRangeMessage(validSpaces);

            setMessages((prev) => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = {
                id: newMessages[newMessages.length - 1].id,
                text: messageText,
                sender: 'bot',
                timestamp: new Date(),
                recommendedSpaces: validSpaces,
                priceRangesSkipped: true, // Flag to indicate price ranges were skipped
              };
              return newMessages;
            });
            setStep(4); // Go directly to space selection
          } else {
            // Multiple spaces with different prices - show price ranges first
            const priceRanges = generatePriceRanges(validSpaces);

            // Only proceed if we have price ranges
            if (priceRanges.length === 0) {
              // Replace loading message with no results message and find something else option
              setMessages((prev) => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1] = {
                  id: newMessages[newMessages.length - 1].id,
                  text: `No price ranges could be generated from the available spaces for ${location}`,
                  sender: 'bot',
                  timestamp: new Date(),
                  showFindSomethingElse: true,
                };
                return newMessages;
              });
              // Reset step to 2 if no price ranges
              setStep(2);
            } else {
              // Replace loading message with price range options
              setMessages((prev) => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1] = {
                  id: newMessages[newMessages.length - 1].id,
                  text: 'Perfect! Here are the available price ranges for your selection:',
                  sender: 'bot',
                  timestamp: new Date(),
                  priceRanges: priceRanges,
                  filteredSpaces: validSpaces,
                };
                return newMessages;
              });

              // Step is already set to 3, no need to set it again
            }
          }
        }
      }
    } catch (error) {
      // Replace loading message with specific error message
      setMessages((prev) => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          id: newMessages[newMessages.length - 1].id,
          text: error.message || "I'm having trouble finding spaces. Please try again later.",
          sender: 'bot',
          timestamp: new Date(),
        };
        return newMessages;
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle price range selection
  const handlePriceRangeSelect = async (priceRange) => {
    const userMsg = {
      id: messages.length + 1,
      text: priceRange.label,
      sender: 'user',
      timestamp: new Date(),
    };

    // Add user message immediately
    setMessages((prev) => [...prev, userMsg]);

    // Update step immediately to prevent flickering
    setStep(4);

    // Show loading spinner
    setLoading(true);
    setMessages((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        text: 'Finding the best spaces for you...',
        sender: 'bot',
        timestamp: new Date(),
        isLoading: true,
      },
    ]);

    try {
      // Get the filtered spaces from the previous message
      const previousMessage = messages.find((msg) => msg.filteredSpaces);
      const filteredSpaces = previousMessage?.filteredSpaces || [];

      // Only proceed if we have actual filtered spaces data
      if (filteredSpaces.length === 0) {
        throw new Error('No filtered spaces data found');
      }

      // Filter spaces based on price range
      const finalSpaces = filterSpacesByPriceRange(filteredSpaces, priceRange);

      // Only proceed if we have final spaces
      if (finalSpaces.length === 0) {
        // Replace loading message with no results message and find something else option
        setMessages((prev) => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            id: newMessages[newMessages.length - 1].id,
            text: `No spaces found within the selected price range: ${priceRange.label}`,
            sender: 'bot',
            timestamp: new Date(),
            showFindSomethingElse: true,
          };
          return newMessages;
        });
      } else {
        // Replace loading message with recommended spaces
        setMessages((prev) => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            id: newMessages[newMessages.length - 1].id,
            text: `Here are the spaces that match your criteria:`,
            sender: 'bot',
            timestamp: new Date(),
            recommendedSpaces: finalSpaces.slice(0, 5), // Show top 5 spaces
          };
          return newMessages;
        });
      }

      // Step is already set to 4, no need to set it again
    } catch (error) {
      // Replace loading message with specific error message
      setMessages((prev) => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          id: newMessages[newMessages.length - 1].id,
          text: error.message || "I'm having trouble finding spaces. Please try again later.",
          sender: 'bot',
          timestamp: new Date(),
        };
        return newMessages;
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    const validation = validateUserInput(inputMessage);
    if (validation.isValid) {
      const userMessage = validation.sanitized;
      const newMessage = createMessage(userMessage, 'user');
      setMessages([...messages, newMessage]);
      setInputMessage('');
      setLoading(true);

      try {
        if (userMessage.toLowerCase() === 'agent' || userMessage.toLowerCase() === 'human') {
          setShowContactForm(true);
          setMessages((prev) => [...prev, createMessage(generateContactAgentMessage(), 'bot')]);
        } else if (userMessage.toLowerCase() === 'help' || userMessage.toLowerCase() === '?') {
          setShowInfo(true);
          setMessages((prev) => [...prev, createMessage(generateHelpMessage(), 'bot')]);
        } else if (isWorkspaceOptionsQuestion(userMessage)) {
          // Show available workspace types with detailed information in organized format
          setMessages((prev) => [
            ...prev,
            {
              id: prev.length + 1,
              text: 'Here are all the available workspace types:',
              sender: 'bot',
              timestamp: new Date(),
              showOrganizedWorkspaceOptions: true,
              workspaceOptions: workspaceOptions,
            },
          ]);
        } else {
          // Check if user is asking about how to proceed with booking
          if (isBookingProcessQuestion(userMessage)) {
            // Debug logging when response is triggered
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Booking process question detected! Triggering response...');
            }
            setMessages((prev) => [...prev, createMessage(generateBookingProcessText(), 'bot')]);
          } else if (isHowToBookQuestion(userMessage)) {
            setMessages((prev) => [...prev, createMessage(generateBookingStepsText(), 'bot')]);
          } else if (isBookingRequest(userMessage)) {
            // Check if user is asking to book something (not about the process)
            setMessages((prev) => [...prev, createMessage(generateBookingRestrictionMessage(), 'bot')]);
          } else {
            // Check if user is asking about a different workspace type
            const userWords = userMessage.toLowerCase().split(' ');
            const mentionedWorkspaceType = workspaceOptions.find((option) => userWords.some((word) => option.label.toLowerCase().includes(word)));

            if (mentionedWorkspaceType && selectedWorkspaceType && mentionedWorkspaceType.value !== selectedWorkspaceType.value) {
              // User is asking about a different workspace type
              setMessages((prev) => [...prev, createMessage(generateWorkspaceTypeRestrictionMessage(selectedWorkspaceType), 'bot')]);
            } else {
              // Build context and send to Gemini
              const context = buildGeminiContext({
                userName,
                bookingCount: previousBookingsCount,
                isLoggedIn,
                selectedWorkspaceType,
                availableWorkspaceTypes: workspaceOptions,
                availableSpaces,
                conversationHistory: messages,
                currentStep: step,
                selectedSpace,
              });
              const botReply = await fetchAgentResponse(userMessage, [], context);

              // Try to parse JSON response from Gemini
              let parsedResponse = null;
              try {
                // Look for JSON content in the response
                const jsonMatch = botReply.match(/```json\s*(\{.*?\})\s*```/s);
                if (jsonMatch) {
                  parsedResponse = JSON.parse(jsonMatch[1]);
                } else {
                  // Try to parse the entire response as JSON
                  parsedResponse = JSON.parse(botReply.trim());
                }
              } catch (e) {
                // If parsing fails, treat as regular text response
                parsedResponse = null;
              }

              // Create message with parsed data or fallback to text
              const messageData = {
                id: messages.length + 1,
                text:
                  parsedResponse && parsedResponse.showAvailableSpaces
                    ? 'Here are the available spaces for your selection:'
                    : parsedResponse
                    ? parsedResponse.text || botReply
                    : botReply,
                sender: 'bot',
                timestamp: new Date(),
              };

              // Add structured data if available
              if (parsedResponse && parsedResponse.showAvailableSpaces && parsedResponse.availableSpaces) {
                messageData.showAvailableSpaces = true;
                messageData.availableSpaces = parsedResponse.availableSpaces;
              }

              setMessages((prev) => [...prev, messageData]);
            }
          }
        }
      } catch (e) {
        setMessages((prev) => [...prev, createMessage(generateErrorMessage(), 'bot')]);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  // Handle contact form change
  const handleContactFormChange = (e) => {
    handleFormFieldChange(e, setContactForm, contactForm);

    // Clear error for the field being changed
    const { name } = e.target;
    if (contactFormErrors[name]) {
      setContactFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Handle contact form submit
  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form before submission
      const validation = validateContactForm(contactForm);
      if (!validation.isValid) {
        // Set validation errors for display (only show field-specific errors, no generic message)
        setContactFormErrors(validation.errors);
        return;
      }

      // Sanitize and format form data
      const sanitizedData = sanitizeFormData(contactForm);
      const formattedData = formatFormDataForSubmission(sanitizedData);

      await dispatch(submitContactForm(formattedData));
      setMessages((prev) => [...prev, createMessage(generateContactFormSuccessMessage(), 'bot')]);
      setShowContactForm(false);
      resetForm(setContactForm);
      setContactFormErrors({});
    } catch (error) {
      setMessages((prev) => [...prev, createMessage(generateContactFormErrorMessage(), 'bot')]);
    } finally {
      setLoading(false);
    }
  };

  // Handle space selection
  const handleSpaceSelect = (space) => {
    // Track the selected space
    setSelectedSpace(space);

    // Get the selected workspace type from the conversation
    const workspaceMessage = messages.find((msg) => msg.selectedWorkspace);
    const selectedWorkspace = workspaceMessage?.selectedWorkspace;

    if (selectedWorkspace) {
      // Get country from URL or default to Sri-Lanka
      const countryCode = getCountryFromUrl();
      const country = getCountryNameFromCode(countryCode);

      // Construct the booking URL
      const eventTypeId = selectedWorkspace.eventTypeId;
      const spaceId = space.id || space.spaceId;
      const unit = space.rateUnit;

      if (!eventTypeId) {
        // Fallback to using the code if eventTypeId is not available
        const fallbackEventTypeId = selectedWorkspace.code;
        const bookingUrl = constructBookingUrl({
          country: country,
          eventTypeId: fallbackEventTypeId,
          spaceId: spaceId,
          unit: unit,
        });

        // Navigate to the booking page
        window.open(bookingUrl, '_blank');
      } else {
        const bookingUrl = constructBookingUrl({
          country: country,
          eventTypeId: eventTypeId,
          spaceId: spaceId,
          unit: unit,
        });

        // Navigate to the booking page
        window.open(bookingUrl, '_blank');
      }

      // Add user selection message
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          text: `Selected: ${space.name}`,
          sender: 'user',
          timestamp: new Date(),
        },
      ]);

      // Add follow-up message with options
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          text: `Perfect! I've opened the booking page for ${space.name}. You can now select your preferred date and time.`,
          sender: 'bot',
          timestamp: new Date(),
        },
        {
          id: prev.length + 2,
          text: 'Would you like to contact our agent for assistance, or would you like to find something else?',
          sender: 'bot',
          timestamp: new Date(),
          showFollowUpOptions: true,
        },
      ]);
    } else {
      // Fallback if workspace option not found
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          text: 'What date do you need the space for?',
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    }

    setStep(5);
  };

  // Handle contact agent after space selection
  const handleContactAgentAfterSpace = () => {
    setMessages((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        text: 'Contact Agent',
        sender: 'user',
        timestamp: new Date(),
      },
    ]);

    setShowContactForm(true);
  };

  // Handle find something else
  const handleFindSomethingElse = () => {
    setMessages((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        text: 'Find Something Else',
        sender: 'user',
        timestamp: new Date(),
      },
    ]);

    // Reset to initial state
    setStep(1);
    setShowContactForm(false);
    setSelectedSpace(null);

    // Show personalized message for logged-in users or default message for non-logged in users
    if (isLoggedIn) {
      const showPersonalizedMessage = async () => {
        try {
          const userData = await fetchUserDataAndBookingsLocal();
          const userDetails = decode(authDetails);

          // Use userName from API if available, otherwise fallback to email
          const displayName = userData.userName || userDetails?.sub || userDetails?.name || 'there';

          // Check if user has previous bookings with eventType
          if (userData.lastBookedSpace && userData.lastBookedSpace.eventType) {
            const previousEventType = userData.lastBookedSpace.eventType;
            const previousLocation = userData.lastBookedSpace.location?.city || userData.lastBookedSpace.location?.address || 'Colombo';
            const welcomeMessage = `Hi ${displayName},Welcome back!,\n\nYou've booked ${userData.bookingCount} time${
              userData.bookingCount > 1 ? 's' : ''
            } with MillionSpaces! We're excited to have you back.\n\nYour previous room was a ${previousEventType}. Book the same type? `;

            setMessages([createPersonalizedWelcomeMessage(displayName, userData.bookingCount, previousEventType, previousLocation)]);
            setStep(1.5); // Special step for previous booking question
          } else {
            // No previous bookings or no eventType, start normal flow
            setMessages([createPersonalizedWelcomeMessage(displayName, userData.bookingCount)]);
            setStep(1); // Normal flow
          }
        } catch (error) {
          // Fallback to default message
          setMessages([createFallbackWelcomeMessage(), createSecondaryWelcomeMessage()]);
        }
      };

      setTimeout(showPersonalizedMessage, 100);
    } else {
      // For non-logged in users, show the default message
      setMessages([createDefaultWelcomeMessage()]);
    }

    // Only fetch workspace options if they're not already loaded
    if (!workspaceOptionsLoaded) {
      fetchWorkspaceOptionsLocal();
    }
  };

  // Handle workspace options question (Yes/No response)
  const handleWorkspaceOptionsQuestion = (response) => {
    // Add user response to chat
    const userResponse = response === 'yes' ? 'Yes, find different workspace' : 'No, continue with chat';
    setMessages((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        text: userResponse,
        sender: 'user',
        timestamp: new Date(),
      },
    ]);

    if (response === 'yes') {
      // Reset chat and initiate again - use existing Redux data like Restart Chat button
      setMessages([]);
      setStep(1);
      setInitialMessageSet(false);
      setShowContactForm(false);
      setSelectedSpace(null);
      setSelectedWorkspaceType(null);
      setAvailableSpaces([]);
      setHasDeclinedPreviousBooking(false);

      // Show personalized message for logged-in users using existing Redux data
      if (isLoggedIn && userData) {
        const displayName = userData.userName || 'there';
        const bookingCount = userData.bookingCount || 0;

        if (userData.lastBookedSpace && userData.lastBookedSpace.eventType && !hasDeclinedPreviousBooking) {
          const previousEventType = userData.lastBookedSpace.eventType;
          const previousLocation = userData.lastBookedSpace.location?.city || userData.lastBookedSpace.location?.address || 'Colombo';
          setMessages([createPersonalizedWelcomeMessage(displayName, bookingCount, previousEventType, previousLocation)]);
          setStep(1.5);
        } else {
          setMessages([createPersonalizedWelcomeMessage(displayName, bookingCount)]);
          setStep(1);
        }
      } else {
        // For non-logged in users, show the default message
        setMessages([createDefaultWelcomeMessage()]);
      }
    } else {
      // Continue with chat - add a bot message to continue the conversation
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          text: 'Great! How can I help you today? You can ask me about workspace types, locations, or anything else related to finding the perfect workspace.',
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    }
  };

  // Effects

  useEffect(() => {
    if (!isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  // Store onClose in a ref to prevent infinite loops
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Effect to handle URL changes and hide chatbot on specific pages
  useEffect(() => {
    const handleUrlChange = () => {
      if (isOpen && shouldHideChatbot()) {
        onCloseRef.current();
      }
    };

    // Check on mount
    handleUrlChange();

    // Listen for URL changes (for SPA navigation)
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [isOpen]); // Removed onClose from dependencies

  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Store dispatch in a ref to prevent infinite loops
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(() => {
    console.log('Chatbot: Login state changed:', { isLoggedIn, userDataLoading, userData: userData ? 'available' : 'null' });

    if (!isLoggedIn) {
      console.log('Chatbot: User logged out, clearing chat state...');
      setInitialMessageSet(false);
      setMessages([]);
      setStep(1);
      setShowContactForm(false);
      setPreviousBookingsCount(0);
      setUserName('');
      setSelectedSpace(null);
      setSelectedWorkspaceType(null);
      setAvailableSpaces([]);
      setHasDeclinedPreviousBooking(false);
      resetForm(setContactForm);
      setContactFormErrors({});

      // Clear user data from Redux when logging out
      dispatchRef.current(clearUserData());
    } else {
      // When user logs in, reset the flags to allow personalized message
      setInitialMessageSet(false);
      setHasDeclinedPreviousBooking(false);

      // Always fetch user data when user logs in to ensure fresh data
      if (!userDataLoading) {
        console.log('Chatbot: User logged in, fetching user data...');
        dispatchRef.current(fetchUserData());
      } else {
        console.log('Chatbot: User logged in but userDataLoading is true, waiting...');
      }

      // Reset messages to prevent showing old messages while loading new user data
      setMessages([]);
      setInitialMessageSet(false);
    }
  }, [isLoggedIn]); // Removed userData from dependencies to prevent infinite loop

  // Auto-load workspace options from Redux when available (for when data loads after popup opens)
  useEffect(() => {
    if (isOpen && !workspaceOptionsLoaded) {
      refreshWorkspaceOptionsFromRedux();
    }
  }, [isOpen, spaceListingTypes, spaceActivities, searchResultTypes, homePageTypes, workspaceOptionsLoaded]);

  // Immediate check on component mount to see if Redux data is already available
  useEffect(() => {
    if (isOpen) {
      const reduxOptions = getWorkspaceOptionsFromRedux();
      if (reduxOptions && reduxOptions.length > 0 && !workspaceOptionsLoaded) {
        // Redux already has data - use it immediately
        setWorkspaceOptions(reduxOptions);
        setWorkspaceOptionsLoaded(true);
        setWorkspaceOptionsSource('redux');
      } else if (!workspaceOptionsLoaded) {
        // No Redux data - fetch from API
        fetchWorkspaceOptionsLocal();
      }
    }
  }, [isOpen]); // Only depend on isOpen, not on Redux state changes

  // Initialize chat when component mounts or when popup opens
  useEffect(() => {
    console.log('Chatbot: Main chat initialization useEffect triggered:', {
      isOpen,
      initialMessageSet,
      workspaceOptionsLoaded,
      isLoggedIn,
      userData: userData ? 'available' : 'null',
      userDataLoading,
    });

    if (isOpen && !initialMessageSet && workspaceOptionsLoaded) {
      if (isLoggedIn) {
        // For logged-in users, wait for user data to be available
        // Don't show any messages until user data is loaded
        console.log('Chatbot: User is logged in, waiting for user data...');
        return;
      } else {
        // For non-logged in users, show the default message
        setMessages([createDefaultWelcomeMessage()]);
        setInitialMessageSet(true);
      }
    }
  }, [isOpen, isLoggedIn, initialMessageSet, workspaceOptionsLoaded, userDataLoading, hasDeclinedPreviousBooking]); // Removed userData from dependencies to prevent infinite loop

  // Watch for userData changes and initialize chat when data becomes available
  useEffect(() => {
    console.log('Chatbot: userData useEffect triggered:', {
      isOpen,
      isLoggedIn,
      initialMessageSet,
      workspaceOptionsLoaded,
      userData: userData ? 'available' : 'null',
      userDataLoading,
      hasDeclinedPreviousBooking,
    });

    if (
      isOpen &&
      isLoggedIn &&
      !initialMessageSet &&
      workspaceOptionsLoaded &&
      userData &&
      userData.userName !== undefined &&
      userData.bookingCount !== undefined
    ) {
      console.log('Chatbot: User data available and welcome delay passed, initializing chat...', userData);

      // Clear any existing messages to prevent duplicates
      setMessages([]);

      // Use Redux data immediately
      const displayName = userData.userName;
      const firstName = displayName?.split(' ')[0];
      const bookingCount = userData.bookingCount;

      // Check if user has previous bookings with eventType and hasn't declined
      if (userData.lastBookedSpace && userData.lastBookedSpace.eventType && !hasDeclinedPreviousBooking) {
        const previousEventType = userData.lastBookedSpace.eventType;
        const previousLocation = userData.lastBookedSpace.location?.city || userData.lastBookedSpace.location?.address || 'Colombo';
        const welcomeMessage = `Hi ${firstName},Welcome back!\n\nYou've booked ${bookingCount} time${
          bookingCount > 1 ? 's' : ''
        } with MillionSpaces! We're excited to have you back.\n\nYour previous room was a ${previousEventType}. Book the same type?`;

        setMessages([
          {
            id: 1,
            text: welcomeMessage,
            sender: 'bot',
            timestamp: new Date(),
            previousEventType: previousEventType,
          },
        ]);
        setStep(1.5); // Special step for previous booking question
      } else {
        // No previous bookings or no eventType, start normal flow
        setMessages([createPersonalizedWelcomeMessage(displayName, userData.bookingCount)]);
        setStep(1); // Normal flow
      }
      setInitialMessageSet(true);
    }
  }, [userData, isOpen, isLoggedIn, initialMessageSet, workspaceOptionsLoaded, hasDeclinedPreviousBooking]);

  // Reset chat state when popup closes to prevent stale data
  useEffect(() => {
    if (!isOpen) {
      setInitialMessageSet(false);
      setMessages([]);
      setStep(1);
    }
  }, [isOpen]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Handle scroll to bottom when component mounts
  useEffect(() => {
    const messagesContainer = document.querySelector('.chatbot-messages');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, []);

  return (
    <>
      {/* Show loading state when popup is open but workspace options are not loaded OR when user data is loading */}
      {isOpen && !shouldHideChatbot() && (!workspaceOptionsLoaded || (isLoggedIn && userDataLoading)) && (
        <div className={`${CSS_CLASSES.OVERLAY}`} onClick={handleBackdropClick} role="presentation">
          <div
            className={`${CSS_CLASSES.POPUP}`}
            onClick={stopPopupClickPropagation}
            role="dialog"
            aria-modal="true"
            aria-label={CHATBOT_CONFIG.NAME}
          >
            <div className="chatbot-loading-container">
              <div className="chatbot-loading-content">
                <div className="chatbot-avatar">
                  <img src={spaceyIconSrc} alt="Spacey" width="48" height="48" />
                </div>
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <span>{isLoggedIn && userDataLoading ? 'Loading your data...' : 'Loading Spacey...'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show main chatbot when workspace options are loaded AND user data is available (for logged-in users) */}
      {isOpen && !shouldHideChatbot() && workspaceOptionsLoaded && (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
        <div
          className={`${CSS_CLASSES.OVERLAY} ${isClosing ? CSS_CLASSES.CLOSING : ''}`}
          onClick={handleBackdropClick}
          role="presentation"
        >
          <div
            className={`${CSS_CLASSES.POPUP} ${isClosing ? CSS_CLASSES.CLOSING : ''}`}
            onClick={stopPopupClickPropagation}
            role="dialog"
            aria-modal="true"
            aria-label={CHATBOT_CONFIG.NAME}
          >
            {/* Chatbot header */}
            <div className="chatbot-header">
              <div className="chatbot-title">
                <div className="chatbot-avatar">
                  <img src={spaceyIconSrc} alt="Spacey" width="32" height="32" />
                </div>
                <div className="chatbot-info">
                  <h3>{CHATBOT_CONFIG.NAME}</h3>
                </div>
              </div>
              <div className="header-actions">
                <button className="reload-button " onClick={handleReloadChat} title="Click here to restart the conversation.">
                  {/* <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z"
                      fill="currentColor"
                    />
                  </svg> */}
                  <img src={require('../../../assets/images/agent/home-reload.svg')} alt="refresh Icon" width="22" height="22" />
                </button>
                <button className="close-button" onClick={handleClose}>
                  {/* <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                      fill="currentColor"
                    />
                  </svg> */}
                  <img src={require('../../../assets/images/agent/close-circle.svg')} alt="close Icon" width="22" height="22" />
                </button>
              </div>
            </div>

            {/* Login prompt */}
            {!isLoggedIn && (
              <div className="chatbot-login-bar">
                <div className="login-bar-content">
                  <div className="login-message">
                    <div className="login-icon">
                      <img src={require('../../../assets/images/agent/shield-icon.svg')} width="30" height="30" alt="shield icon" />
                    </div>
                    <span className="login-text">Please log in to get personalized workspace recommendations</span>
                  </div>
                  <button className="login-button" onClick={handleLogin}>
                    Log In
                  </button>
                </div>
              </div>
            )}

            {/* Show loading message for logged-in users while waiting for user data */}
            {isLoggedIn && userDataLoading && (
              <div className="chatbot-loading-message">
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <span>Loading your personalized experience...</span>
                </div>
              </div>
            )}

            {/* Chat messages */}
            {(!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
              <div className="chatbot-messages">
                {/* Show actual messages only when workspace options are loaded */}
                {messages.map((message, index) => (
                  <div key={message.id} className={`message ${message.sender}`}>
                    <div className={`${message.id == 1 ? 'initial-message-content' : 'message-content'}`}>
                      {message.isLoading ? (
                        <div className="loading-spinner">
                          <div className="spinner"></div>
                          <span>{message.text}</span>
                        </div>
                      ) : (
                        <div className="initial-message-row ">
                          <div className="message-text">
                            {/* Custom rendering for first bot message */}
                            {message.id == 1 ? (
                              <div className="message-text">
                                {(() => {
                                  const parts = message.text.split('\n\n');
                                  return (
                                    <>
                                      <strong>{parts[0]}</strong> <br /> {parts.slice(1).join(' ')}
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div className="message-text">{message.text}</div>
                            )}
                          </div>

                          {message.id == 1 && (
                            <div className="initial-message-avatar">
                              <img
                                src={spaceyIconSrc}
                                width="100"
                                height="100"
                                marginTop="10"
                                alt="Chatbot Avatar"
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Render Yes/No options for previous booking */}
                      {step === CHAT_STEPS.PREVIOUS_BOOKING_QUESTION &&
                        message.previousEventType &&
                        (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
                          <div className="chatbot-options">
                            <button onClick={handlePreviousBookingYes} className={CSS_CLASSES.WORKSPACE_OPTION_BTN_YES}>
                              {BUTTON_ACTIONS.YES}
                            </button>
                            <button onClick={handlePreviousBookingNo} className={CSS_CLASSES.WORKSPACE_OPTION_BTN_NO}>
                              {BUTTON_ACTIONS.NO}
                            </button>
                          </div>
                        )}

                      {/* Render workspace options */}
                      {step === 1 &&
                        workspaceOptionsLoaded &&
                        Array.isArray(workspaceOptions) &&
                        workspaceOptions.length > 0 &&
                        !workspaceOptionsLoading &&
                        (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
                          <div className="chatbot-options">
                            <div className={CSS_CLASSES.OPTIONS_TITLE}>{WORKSPACE_MESSAGES.OPTIONS_TITLE}</div>
                            {workspaceOptions.map((option, index) => (
                              <button key={index} onClick={() => handleWorkspaceSelect(option.value)} className={CSS_CLASSES.WORKSPACE_OPTION_BTN}>
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}

                      {/* Render organized workspace options when user asks about options */}
                      {message.showOrganizedWorkspaceOptions &&
                        message.workspaceOptions &&
                        message.workspaceOptions.length > 0 &&
                        (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
                          <div className="chatbot-options">
                            <div className={CSS_CLASSES.OPTIONS_TITLE}>Available Workspace Types</div>
                            {workspaceOptions.map((option, index) => (
                              <div key={index} className={CSS_CLASSES.WORKSPACE_OPTION_BTN}>
                                {option.label}
                              </div>
                            ))}

                            {/* Question about continuing or starting new search */}
                            <div className="workspace-options-question">
                              <div className="question-text">Shall we continue with these workspaces, or start a new search?</div>
                              <div className="question-options">
                                <button onClick={() => handleWorkspaceOptionsQuestion('yes')} className={CSS_CLASSES.WORKSPACE_OPTION_BTN}>
                                  Yes, find different workspace
                                </button>
                                <button onClick={() => handleWorkspaceOptionsQuestion('no')} className={CSS_CLASSES.WORKSPACE_OPTION_BTN}>
                                  No, continue with chat
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* Render detailed space information responses from Gemini */}
                      {message.showAvailableSpaces &&
                        message.availableSpaces &&
                        message.availableSpaces.length > 0 &&
                        (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
                          <div className="chatbot-options">
                            <div className={CSS_CLASSES.OPTIONS_TITLE}>Available Spaces</div>
                            {message.availableSpaces.map((space, index) => (
                              <div key={index} className={CSS_CLASSES.AVAILABLE_SPACES_CARD}>
                                {/* Space Name */}
                                <h4 className={CSS_CLASSES.AVAILABLE_SPACES_NAME}>{space.name}</h4>

                                {/* Location */}
                                <div className={CSS_CLASSES.AVAILABLE_SPACES_LOCATION}>{space.location}</div>

                                {/* Price */}
                                <div className={CSS_CLASSES.AVAILABLE_SPACES_PRICE}>LKR {space.price}</div>
                              </div>
                            ))}
                          </div>
                        )}

                      {/* Render location options */}
                      {step === CHAT_STEPS.LOCATION_SELECTION &&
                        message.dynamicLocationOptions &&
                        message.dynamicLocationOptions.length > 0 &&
                        (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
                          <div className="chatbot-options">
                            <div className={CSS_CLASSES.OPTIONS_TITLE}>{LOCATION_MESSAGES.TITLE}</div>
                            <select onChange={(e) => handleLocationSelect(e.target.value)} className="location-dropdown" defaultValue="">
                              <option value="" disabled>
                                {LOCATION_MESSAGES.SELECT_PLACEHOLDER}
                              </option>
                              {message.dynamicLocationOptions.map((option, index) => (
                                <option key={index} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                      {/* Render price range options */}
                      {step === CHAT_STEPS.PRICE_RANGE_SELECTION &&
                        message.priceRanges &&
                        message.priceRanges.length > 0 &&
                        (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
                          <div className="chatbot-options">
                            <div className={CSS_CLASSES.OPTIONS_TITLE}>{PRICE_RANGE_MESSAGES.TITLE}</div>
                            {message.priceRanges.map((option, index) => (
                              <button key={index} onClick={() => handlePriceRangeSelect(option)} className={CSS_CLASSES.WORKSPACE_OPTION_BTN}>
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}

                      {/* Render recommended spaces */}
                      {message.recommendedSpaces &&
                        message.recommendedSpaces.length > 0 &&
                        (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
                          <div className="chatbot-options">
                            <div className={CSS_CLASSES.OPTIONS_TITLE}>{SPACE_SELECTION_MESSAGES.RECOMMENDED_TITLE}</div>

                            {message.recommendedSpaces.map((space, index) => {
                              const isSelected = selectedSpace && selectedSpace.id === space.id;
                              return (
                                <button
                                  key={index}
                                  onClick={() => handleSpaceSelect(space)}
                                  className={`${CSS_CLASSES.SPACE_SUGGESTION_OPTION} ${isSelected ? CSS_CLASSES.SELECTED : ''}`}
                                >
                                  <div className={CSS_CLASSES.SPACE_OPTION_CONTENT}>
                                    <div className={CSS_CLASSES.SPACE_IMAGE}>
                                      {space.image ? (
                                        <img
                                          src={/^https?:\/\//i.test(space.image) ? space.image : `${IMAGES_BASE_URL}${space.image}`}
                                          alt={space.name}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'block';
                                          }}
                                        />
                                      ) : null}
                                      {(!space.image || space.image === '') && (
                                        <div className={CSS_CLASSES.SPACE_PLACEHOLDER}>{UI_CONSTANTS.SPACE_PLACEHOLDER_EMOJI}</div>
                                      )}
                                    </div>
                                    <div className={CSS_CLASSES.SPACE_DETAILS}>
                                      <div className={CSS_CLASSES.SPACE_NAME}>{space.name}</div>
                                      <div className={CSS_CLASSES.SPACE_PRICE}>LKR {space.rate}</div>
                                    </div>
                                  </div>
                                  {isSelected && <span className={CSS_CLASSES.SELECTED_INDICATOR}>{UI_CONSTANTS.SELECTED_INDICATOR}</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}

                      {/* Render follow-up options after space selection */}
                      {message.showFollowUpOptions && (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
                        <div className="chatbot-options">
                          <div className={CSS_CLASSES.OPTIONS_TITLE}>{FOLLOW_UP_MESSAGES.TITLE}</div>
                          <button onClick={handleContactAgentAfterSpace} className={CSS_CLASSES.CONTACT_AGENT_OPTION}>
                            {FOLLOW_UP_MESSAGES.CONTACT_AGENT}
                          </button>
                          <button onClick={handleReloadChat} className={CSS_CLASSES.FIND_SOMETHING_ELSE_OPTION}>
                            {FOLLOW_UP_MESSAGES.FIND_SOMETHING_ELSE}
                          </button>
                        </div>
                      )}

                      {/* Render find something else message */}
                      {message.showFindSomethingElse && (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
                        <div className="chatbot-options">
                          <div className={CSS_CLASSES.OPTIONS_TITLE}>{FOLLOW_UP_MESSAGES.FIND_SOMETHING_ELSE_TITLE}</div>
                          <button onClick={handleFindSomethingElse} className={CSS_CLASSES.FIND_SOMETHING_ELSE_OPTION}>
                            {FOLLOW_UP_MESSAGES.FIND_SOMETHING_ELSE}
                          </button>
                        </div>
                      )}
                    </div>
                    <span className="message-time">{formatTimestamp(message.timestamp)}</span>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}

            {/* Contact form */}
            {showContactForm && (!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
              <div className={CSS_CLASSES.CONTACT_FORM}>
                <h4>{CONTACT_FORM_MESSAGES.TITLE}</h4>

                {/* Agent Contact Details */}
                <div className={CSS_CLASSES.AGENT_CONTACT_DETAILS}>
                  <div className={CSS_CLASSES.AGENT_CONTACT_MESSAGE}>{CONTACT_FORM_MESSAGES.AGENT_MESSAGE}</div>
                  <div className={CSS_CLASSES.AGENT_PHONE_NUMBER}>
                    <a href={`tel:${CONTACT_FORM_MESSAGES.AGENT_PHONE}`} className={CSS_CLASSES.PHONE_LINK}>
                      {CONTACT_FORM_MESSAGES.AGENT_PHONE}
                    </a>
                  </div>
                </div>

                <form onSubmit={handleContactFormSubmit}>
                  <div className={CSS_CLASSES.FORM_GROUP}>
                    <label className={CSS_CLASSES.FORM_LABEL}>{FORM_LABELS.NAME}</label>
                    <input
                      type={INPUT_TYPES.TEXT}
                      name={FORM_FIELD_NAMES.NAME}
                      placeholder={FORM_PLACEHOLDERS.NAME}
                      value={contactForm.name}
                      onChange={handleContactFormChange}
                      required
                      className={contactFormErrors.name ? CSS_CLASSES.ERROR : ''}
                    />
                    {contactFormErrors.name && <div className={CSS_CLASSES.FORM_ERROR}>{contactFormErrors.name}</div>}
                  </div>
                  <div className={CSS_CLASSES.FORM_GROUP}>
                    <label className={CSS_CLASSES.FORM_LABEL}>{FORM_LABELS.EMAIL}</label>
                    <input
                      type={INPUT_TYPES.EMAIL}
                      name={FORM_FIELD_NAMES.EMAIL}
                      placeholder={FORM_PLACEHOLDERS.EMAIL}
                      value={contactForm.email}
                      onChange={handleContactFormChange}
                      required
                      className={contactFormErrors.email ? CSS_CLASSES.ERROR : ''}
                    />
                    {contactFormErrors.email && <div className={CSS_CLASSES.FORM_ERROR}>{contactFormErrors.email}</div>}
                  </div>
                  <div className={CSS_CLASSES.FORM_GROUP}>
                    <label className={CSS_CLASSES.FORM_LABEL}>
                      {FORM_LABELS.PHONE_NUMBER} <span className={CSS_CLASSES.OPTIONAL_TEXT}>(Optional)</span>
                    </label>
                    <input
                      type={INPUT_TYPES.TEL}
                      name={FORM_FIELD_NAMES.PHONE_NUMBER}
                      placeholder={FORM_PLACEHOLDERS.PHONE_NUMBER}
                      value={contactForm.phoneNumber}
                      onChange={handleContactFormChange}
                      className={contactFormErrors.phoneNumber ? CSS_CLASSES.ERROR : ''}
                    />
                    {contactFormErrors.phoneNumber && <div className={CSS_CLASSES.FORM_ERROR}>{contactFormErrors.phoneNumber}</div>}
                  </div>
                  <div className={CSS_CLASSES.FORM_GROUP}>
                    <label className={CSS_CLASSES.FORM_LABEL}>{FORM_LABELS.MESSAGE}</label>
                    <textarea
                      name={FORM_FIELD_NAMES.MESSAGE}
                      placeholder={FORM_PLACEHOLDERS.MESSAGE}
                      value={contactForm.message}
                      onChange={handleContactFormChange}
                      required
                      className={contactFormErrors.message ? CSS_CLASSES.ERROR : ''}
                    />
                    {contactFormErrors.message && <div className={CSS_CLASSES.FORM_ERROR}>{contactFormErrors.message}</div>}
                  </div>
                  <div className={CSS_CLASSES.FORM_ACTIONS}>
                    <button type={INPUT_TYPES.SUBMIT} disabled={loading || !isFormReadyToSubmit(contactForm)}>
                      {loading ? LOADING_MESSAGES.SENDING_MESSAGE : FORM_BUTTONS.SEND_MESSAGE}
                    </button>
                    <button
                      type={INPUT_TYPES.BUTTON}
                      onClick={() => {
                        setShowContactForm(false);
                        resetForm(setContactForm);
                        setContactFormErrors({});
                      }}
                    >
                      {FORM_BUTTONS.CANCEL}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Chat input */}
            {!showContactForm && step >= 4 && isLoggedIn && userData && !userDataLoading && (
              <div className="chatbot-input">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  disabled={loading}
                />
                <button onClick={handleSendMessage} disabled={loading || !inputMessage.trim()}>
                  {loading ? (
                    '⏳'
                  ) : (
                    <div className="input-send">
                      <img src={require('../../../assets/images/agent/send.svg')} alt="send button" width="23" height="23" />
                    </div>
                  )}
                </button>
              </div>
            )}

            {/* Disclaimer and Info Section */}
            {(!isLoggedIn || (isLoggedIn && userData && !userDataLoading)) && (
              <div className="chatbot-disclaimer">
                <div className="disclaimer-content">
                  <span className="disclaimer-text">{DISCLAIMER_MESSAGES.TEXT}</span>
                  <button className="info-button" onClick={() => setShowInfo(!showInfo)} title="Click for more information">
                    {/* <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z"
                        fill="currentColor"
                      />
                    </svg> */}

                    <img src={require('../../../assets/images/agent/info-circle.svg')} alt="Info Icon" width="16" height="16" />
                  </button>
                </div>

                {/* Info Modal */}
                {showInfo && (
                  <div className="info-modal">
                    <div className="info-content">
                      <div className="info-header">
                        <h4>{INFO_MESSAGES.ABOUT_SPACEY}</h4>
                        <button className="close-icon" onClick={() => setShowInfo(false)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                              fill="currentColor"
                            />
                          </svg>
                        </button>
                      </div>
                      <div className="info-body">
                        <p>{INFO_PANEL_MESSAGES.DESCRIPTION}</p>

                        <h5>{INFO_PANEL_MESSAGES.CHAT_COMMANDS_TITLE}</h5>
                        <ul>
                          <li>
                            <strong>
                              "{CHAT_COMMANDS.HELP.COMMAND}" or "{CHAT_COMMANDS.HELP.ALTERNATIVE}"
                            </strong>{' '}
                            - {CHAT_COMMANDS.HELP.DESCRIPTION}
                          </li>
                          <li>
                            <strong>"{CHAT_COMMANDS.OPTIONS.COMMAND}"</strong> - {CHAT_COMMANDS.OPTIONS.DESCRIPTION}
                          </li>
                          <li>
                            <strong>
                              "{CHAT_COMMANDS.AGENT.COMMAND}" or "{CHAT_COMMANDS.AGENT.ALTERNATIVE}"
                            </strong>{' '}
                            - {CHAT_COMMANDS.AGENT.DESCRIPTION}
                          </li>
                          <li>
                            <strong>Ask about spaces</strong> - {CHAT_COMMANDS.ASK_SPACES.DESCRIPTION}
                          </li>
                        </ul>

                        <h5>{INFO_PANEL_MESSAGES.CONTACT_AGENT_TITLE}</h5>
                        <ul>
                          <li>{HELP_INFORMATION.CONTACT_AGENT[0]}</li>
                          <li>{HELP_INFORMATION.CONTACT_AGENT[1]}</li>
                          <li>{HELP_INFORMATION.CONTACT_AGENT[2]}</li>
                        </ul>

                        <h5>{INFO_PANEL_MESSAGES.HELP_WITH_TITLE}</h5>
                        <ul>
                          <li>{HELP_INFORMATION.CAN_HELP_WITH[0]}</li>
                          <li>{HELP_INFORMATION.CAN_HELP_WITH[1]}</li>
                          <li>{HELP_INFORMATION.CAN_HELP_WITH[2]}</li>
                          <li>{HELP_INFORMATION.CAN_HELP_WITH[3]}</li>
                          <li>{HELP_INFORMATION.CAN_HELP_WITH[4]}</li>
                        </ul>

                        <h5>{INFO_PANEL_MESSAGES.IMPORTANT_NOTES_TITLE}</h5>
                        <ul>
                          <li>{HELP_INFORMATION.IMPORTANT_NOTES[0]}</li>
                          <li>{HELP_INFORMATION.IMPORTANT_NOTES[1]}</li>
                          <li>{HELP_INFORMATION.IMPORTANT_NOTES[2]}</li>
                        </ul>

                        {/* Debug section - only visible in development */}
                        {process.env.NODE_ENV === 'development' && (
                          <div
                            style={{ marginTop: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}
                          >
                            <h6 style={{ margin: '0 0 10px 0', color: '#666' }}>🔧 Debug Tools</h6>
                            <button
                              onClick={manualRefreshWorkspaceOptions}
                              style={{
                                padding: '8px 12px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                marginRight: '8px',
                              }}
                            >
                              Refresh Workspace Options from Redux
                            </button>
                            <button
                              onClick={clearDeclinedState}
                              style={{
                                padding: '8px 12px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              Reset Previous Booking Question
                            </button>
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
                              Current source: {workspaceOptionsSource} | Declined state: {hasDeclinedPreviousBooking ? 'Yes' : 'No'}
                            </div>
                          </div>
                        )}

                        <button className={CSS_CLASSES.CLOSE_INFO} onClick={() => setShowInfo(false)}>
                          {INFO_PANEL_MESSAGES.GOT_IT}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
});

ChatbotPopup.displayName = 'ChatbotPopup';

export default ChatbotPopup;
